import { Box, Button, Flex, Text } from "@chakra-ui/react";
import { CONNECT_WALLET, STABLE_VAULT } from "@constants/analytics";
import {
  APPROVE_CTA,
  CONNECT_WALLET_CTA,
  MAX_FRACTION_DIGITS,
  SWITCH_NETWORK,
  WITHDRAW_CTA,
  toFixedWithoutRounding,
} from "@constants/common";
import { EVENTS } from "@constants/reduxEvents";
import useDappConfig from "@hooks/useDappConfig";
import useStableAssetExchangeRate from "@hooks/useStableAssetExchangeRate";
import useStableVaultXTokenAllowance from "@hooks/useStableVaultXTokenAllowance";
import useTokenBalance from "@hooks/useTokenBalance";
import { useConnectModal } from "@rainbow-me/rainbowkit";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import {
  emitReduxEvent,
  formatNumber,
  isFloat,
  toStringPrecision,
  trackEvent,
} from "utils";
import { useAccount } from "wagmi";
import { STABLE_TEXT_STRINGS } from "../../contants";
import StableOutputTokenSelect from "../StableOutputTokenSelect";
import StableTicketInput from "../StableTicketInput";
import StableTicketInputBalance from "../StableTicketInputBalance";
import StableTokenSelect from "../StableTokenSelect";

const StableWithdraw = () => {
  const [amount, setAmount] = useState("");
  const [error, setError] = useState("");
  const [selectedOutputToken, setSelectedOutputToken] = useState("USDC");

  const { isConnected, address } = useAccount();
  const { stableVaultConfig, isStableVaultSupportedChain } = useDappConfig();

  const { xTokenStableVaultBalance, tokensBalances } = useTokenBalance();

  const { xTokenAllowance } = useStableVaultXTokenAllowance();

  const { stableFormResetNonce } = useSelector((s: any) => s.user);

  const tokenBalance = xTokenStableVaultBalance;

  const outputTokenBalance = useMemo(() => {
    return tokensBalances[selectedOutputToken] || 0;
  }, [tokensBalances, selectedOutputToken]);

  const { openConnectModal } = useConnectModal();

  const insufficientFund = false;

  const isApproved = useMemo(() => {
    return (
      xTokenAllowance &&
      Number(xTokenAllowance) &&
      Number(xTokenAllowance) >= Number(amount)
    );
  }, [xTokenAllowance, amount]);

  const checkInput = useCallback(
    (value = amount.trim()) => {
      if (!isConnected) return "";
      let error = "";
      if (!+value || Number.isNaN(+value)) {
        error =
          "This field can't be blank or zero. Please provide valid input.";
      } else if (Number(value) > tokenBalance) {
        error = `You can't withdraw more than available balance in your wallet.`;
      }
      return error;
    },
    [isConnected, tokenBalance, amount]
  );

  useEffect(() => {
    if (amount !== "") {
      const error = checkInput(amount);
      setError(error);
    }
  }, [tokenBalance, address, amount, checkInput]);

  // Clear amount & error when Services reports a successful withdraw
  useEffect(() => {
    if (stableFormResetNonce) {
      setAmount("");
      setError("");
    }
  }, [stableFormResetNonce]);

  const getButtonText = () => {
    if (!isConnected) {
      return CONNECT_WALLET_CTA;
    } else if (!isStableVaultSupportedChain) {
      return SWITCH_NETWORK;
    } else if (!isApproved) {
      return APPROVE_CTA;
    } else {
      return WITHDRAW_CTA;
    }
  };

  const stableAssetExchangeRate = useStableAssetExchangeRate();

  const convertedAmount = useMemo(() => {
    if (amount && !isNaN(Number(amount))) {
      const exchangeRate =
        selectedOutputToken === "USDC" || selectedOutputToken === "USDT"
          ? Number(stableAssetExchangeRate?.toERC20)
          : Number(stableAssetExchangeRate?.toERC20);

      const value = Number(amount) * exchangeRate;
      const formatedValue =
        value && isFloat(value)
          ? toFixedWithoutRounding(
              toStringPrecision(value),
              MAX_FRACTION_DIGITS
            )
          : toStringPrecision(value);
      if (!Number.isNaN(+formatedValue)) {
        return formatedValue;
      } else {
        return "0";
      }
    } else {
      return "0";
    }
  }, [amount, stableAssetExchangeRate, selectedOutputToken]);

  const handleClick = useCallback(() => {
    if (!isConnected) {
      // emitReduxEvent(EVENTS.OPEN_WALLET_MODAL);
      openConnectModal?.();
      trackEvent(CONNECT_WALLET, {
        type: STABLE_VAULT.WITHDRAW_CTA,
      });
    } else if (!isStableVaultSupportedChain) {
      emitReduxEvent(EVENTS.SWITCH_NETWORK);
    } else {
      if (isApproved) {
        if (!amount) {
          setError(
            "This field can't be blank or zero. Please provide valid input."
          );
          return;
        }
        emitReduxEvent(EVENTS.HANDLE_STABLE_VAULT_WITHDRAW, {
          amount: amount,
          amountOut: convertedAmount,
          outputToken: selectedOutputToken,
        });

        // Clear amount after request
        setAmount("");
      } else {
        emitReduxEvent(EVENTS.HANDLE_STABLE_VAULT_XTOKEN_APPROVE, {
          amount: amount,
          amountOut: convertedAmount,
          outputToken: selectedOutputToken,
        });
      }
    }
  }, [
    isConnected,
    amount,
    openConnectModal,
    isApproved,
    selectedOutputToken,
    convertedAmount,
  ]);

  const handleChange = (value: string) => {
    const val = value.trim().replace(/,/g, "");
    if (!isNaN(Number(val)) || val === "") {
      if (val.indexOf(".") === -1) {
        setAmount(val);
      } else {
        if (val.length - (val.indexOf(".") + 1) <= MAX_FRACTION_DIGITS) {
          setAmount(val === "" ? "0." : val);
        }
      }
    }
  };

  const handleMaxClick = useCallback(() => {
    setAmount(toFixedWithoutRounding(tokenBalance.toString(), 4));
  }, [tokenBalance]);

  return (
    <Flex
      fontSize="14px"
      lineHeight="20px"
      fontWeight="400"
      pt="20px"
      direction={"column"}
    >
      <Flex
        justifyContent={"space-between"}
        mx="16px"
        mb="15px"
        pt={{ base: "10px", md: "20px" }}
        px={{ base: "8px", md: "16px" }}
        pb={{ base: "10px", md: insufficientFund ? "0px" : "20px" }}
        gap={{ base: "20px", md: "12px" }}
        direction={{ base: "column", md: "row" }}
      >
        <Flex
          width={{ base: "100%", md: "38%" }}
          minW={{ base: "100%", md: "251px" }}
          direction={"column"}
        >
          <Box h="12px">
            <Text fontSize="12px" lineHeight="14.4px" fontWeight="400">
              Withdraw from vault
            </Text>
          </Box>

          <Flex>
            <StableTicketInput
              inputRightElement={
                <StableTokenSelect selected="sbUSD" disabled />
              }
              value={amount}
              onChange={(value: string) => handleChange(value)}
              error={error}
            />
          </Flex>

          <Flex mt="8px">
            <StableTicketInputBalance
              label="You have"
              tokenAmount={tokenBalance}
              onClick={handleMaxClick}
              isConnected={isConnected}
              token="sbUSD"
            />
          </Flex>
        </Flex>

        <Flex
          direction={"column"}
          width={{ base: "100%", md: "38%" }}
          minW={{ base: "100%", md: "251px" }}
        >
          <Box h="12px">
            <Text fontSize="12px" lineHeight="14.4px" fontWeight="400">
              To your wallet
            </Text>
          </Box>

          <Flex>
            <StableTicketInput
              inputRightElement={
                <StableOutputTokenSelect
                  disabled={false}
                  selected={selectedOutputToken}
                  onTokenSelect={setSelectedOutputToken}
                  tokenBgColor="#ECECEC"
                />
              }
              value={
                formatNumber(
                  Number(convertedAmount),
                  false,
                  MAX_FRACTION_DIGITS,
                  true
                ) || "0"
              }
              // onChange={(value: string) => handleChange(value)}
              error={error}
              disabled={true}
            />
          </Flex>
          <Flex mt="8px">
            <StableTicketInputBalance
              label="Balance"
              tokenAmount={outputTokenBalance}
              token={selectedOutputToken}
              onClick={() => {}}
              isConnected={isConnected}
              hideMax
            />
          </Flex>
        </Flex>

        <Flex alignItems={"center"} justifyContent={"center"}>
          <Button
            borderRadius="12px"
            bg="textPrimary"
            p="8px 16px"
            h="48px"
            mt={{ base: 0, md: isConnected ? "-10px" : "12px" }}
            minW={"131.875px"}
            width="100%"
            _hover={{
              bg: "textPrimary",
            }}
            onClick={handleClick}
            isDisabled={isConnected && (error !== "" || amount === "")}
          >
            <Text
              fontSize="16px"
              lineHeight="24px"
              fontWeight="600"
              color="backgroundCard"
            >
              {getButtonText()}
            </Text>
          </Button>
        </Flex>
      </Flex>

      {!insufficientFund && error && (
        <Flex
          bgColor="#FF616A"
          p="12px 24px"
          borderRadius={"6px"}
          m={{ base: "0 24px 24px 24px", md: "0 32px 24px 32px" }}
        >
          <Text
            color="white"
            fontSize={"13px"}
            fontWeight={"500"}
            lineHeight={"15.6px"}
          >
            {error}
          </Text>
        </Flex>
      )}

      {insufficientFund && (
        <Flex
          bgColor="#FF616A"
          p="12px 24px"
          borderRadius={"6px"}
          m={{ base: "0 24px 24px 24px", md: "0 32px 24px 32px" }}
        >
          <Text
            color="white"
            fontSize={"13px"}
            fontWeight={"500"}
            lineHeight={"15.6px"}
          >
            Insufficient Balance. Please try again
          </Text>
        </Flex>
      )}

      <Box
        w={"100%"}
        // position={"absolute"}
        borderTop="1px solid"
        borderColor="gray.200"
        pb={2}
        px="30px"
      >
        <Flex>
          <Flex
            gap={{ base: 0, md: "10px" }}
            direction={{ base: "column", md: "row" }}
          >
            {stableAssetExchangeRate.toERC20 ? (
              <Flex mt="1rem" gap="4px">
                <Text fontSize={"12px"} fontWeight={"400"} color="#A1B6BA">
                  Exchange rate
                </Text>
                <Text fontSize={"12px"} fontWeight={"600"} color="#3F6870">
                  {`1 ${stableVaultConfig?.xToken?.symbol} = ${
                    Number(stableAssetExchangeRate.toERC20) === 1
                      ? "1"
                      : toFixedWithoutRounding(
                          stableAssetExchangeRate.toERC20.toString(),
                          4
                        )
                  } ${selectedOutputToken}`}
                </Text>
              </Flex>
            ) : null}
            {/* {conversionsDataHgEth.toERC20 ? (
              <Flex mt={{ base: "4px", md: "1rem" }} gap="4px">
                <Text fontSize={"12px"} fontWeight={"400"} color="#A1B6BA">
                  Withdrawal limit
                </Text>
                <Text fontSize={"12px"} fontWeight={"600"} color="#3F6870">
                  21.11 agETH
                </Text>
              </Flex>
            ) : null} */}
          </Flex>
        </Flex>

        <Flex mt="6px" direction="column" gap="4px">
          <Text
            fontSize={"10px"}
            lineHeight={"12px"}
            fontWeight={"400"}
            letterSpacing={"0.5%"}
            color="#A1B6BA"
          >
            {STABLE_TEXT_STRINGS.SECTIONS.DEPOSIT_WITHDRAW.DISCLAIMER.WITHDRAW}
          </Text>
          <Text
            fontSize={"10px"}
            lineHeight={"12px"}
            fontWeight={"400"}
            letterSpacing={"0.5%"}
            color="#A1B6BA"
          >
            Withdrawals require a 3-day processing period. Check the Claims tab
            to view pending and claimable withdrawals.
          </Text>
        </Flex>
      </Box>
    </Flex>
  );
};

export default StableWithdraw;
